<template>
  <div class="flex flex-col items-center justify-center h-full">
    <div
      id="pageWrapper"
      :class="{
        'items-stretch my-6 w-mobile-container max-h-mobile-container rounded-2.25xl border-2 bg-bg border-bg overflow-hidden':
          !isMobile,
        'flex-col w-full': isMobile,
      }"
      class="relative flex flex-grow items-center justify-between h-full bg-bg"
    >
      <a
        v-if="showCancelButton"
        href="about:blank"
        :class="[
          'absolute',
          'z-20',
          'left-3',
          'top-3',
          resignLinkTextColor,
          'hover:no-underline',
          'underline',
          'text-sm',
          'leading-none',
          'opacity-60',
        ]"
      >
        {{ $t('common.resign') }}
      </a>
      <slot name="logo" />

      <div
        id="contentContainer"
        :class="{
          'flex-grow absolute h-screen': isFullscreenDropdown && isMobile,
          'rounded-2xl relative': !isMobile,
          'min-h-mobile-content': isMobile,
        }"
        class="z-10 flex flex-col w-full bg-white overflow-y-auto"
      >
        <div class="form-wrapper" :class="{ static: isMobile }">
          <slot name="form" />
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import isMobile from '@/utils/isMobile';

@Component({
  name: 'survey-page-wrapper',
})
export default class SurveyPageWrapper extends Vue {
  @Prop({ default: false, type: Boolean })
  private showCancelButton!: boolean;

  private isMobile!: boolean;
  private backgroundColorTrigger: number = 0;

  created() {
    this.isMobile = isMobile();
  }

  private get isFullscreenDropdown(): boolean {
    return this.$store.state.isFullscreenDropdown;
  }

  private get resignLinkTextColor(): string {
    // Force reactivity by accessing the trigger
    this.backgroundColorTrigger;

    // Get the computed background color from CSS variable
    if (typeof window === 'undefined') return 'text-white'; // SSR fallback

    const pageWrapper = document.getElementById('pageWrapper');
    if (!pageWrapper) return 'text-black'; // fallback to black for light backgrounds

    const computedStyle = window.getComputedStyle(pageWrapper);
    const bgColor = computedStyle.backgroundColor;

    // If background color is not set or transparent, default to black text (for light backgrounds)
    if (
      !bgColor ||
      bgColor === 'transparent' ||
      bgColor === 'rgba(0, 0, 0, 0)'
    ) {
      return 'text-black';
    }

    // Parse RGB values from the computed style
    const rgbMatch = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!rgbMatch) {
      return 'text-black';
    }

    const r = parseInt(rgbMatch[1]);
    const g = parseInt(rgbMatch[2]);
    const b = parseInt(rgbMatch[3]);

    // Calculate luminance to determine if background is light or dark
    // Using simple brightness calculation: (R*299 + G*587 + B*114) / 1000
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // If brightness > 127, background is light, use dark text
    // If brightness <= 127, background is dark, use light text
    return brightness > 127 ? 'text-black' : 'text-white';
  }

  private updateTextColor(): void {
    this.backgroundColorTrigger++;
  }

  private mounted() {
    window.addEventListener('scroll', this.scrollEventHandler);
    // Update text color after DOM is ready
    this.$nextTick(() => {
      this.updateTextColor();
    });
  }

  private beforeDestroy() {
    window.removeEventListener('scroll', this.scrollEventHandler);
  }

  private scrollEventHandler() {
    if (!this.isMobile) return;
    const logoEl = document.getElementById('logoWrapper');
    if (!logoEl) return;
    const pageWrapper = document.getElementById('pageWrapper');
    const logoHeight = logoEl?.getClientRects()[0].height;
    pageWrapper?.style.setProperty(
      '--opacity',
      `${1 - window.scrollY / logoHeight}`
    );
    pageWrapper?.style.setProperty(
      '--translateY',
      `-${(window.scrollY / logoHeight) * 65}px`
    );
  }
}
</script>
<style>
.form-wrapper {
  @apply relative flex flex-col flex-grow flex-shrink-0;
}
</style>
